import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SkelList } from "@/components/Skeleton";
import { applyTheme, getStoredTheme } from "@/lib/theme";
import SeoManager from "@/lib/seo";
import AnalyticsBridge from "@/components/AnalyticsBridge";

// SEO round 2 — CustomerApp (the full ordering flow) now code-splits too: /menu and
// /privacy no longer download/parse it, and "/" gets it via Suspense with a skeleton.
const CustomerApp = lazy(() => import("@/pages/CustomerApp"));
const StaffLogin = lazy(() => import("@/pages/StaffLogin"));
const StaffPortal = lazy(() => import("@/pages/StaffPortal"));
const StaffInvite = lazy(() => import("@/pages/StaffInvite"));
const SetPassword = lazy(() => import("@/pages/SetPassword"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const PrivacyNotice = lazy(() => import("@/pages/PrivacyNotice"));
const ClaimScreen = lazy(() => import("@/pages/ClaimScreen"));
const InvoicePrint = lazy(() => import("@/pages/InvoicePrint"));
const DocketBatchPrint = lazy(() => import("@/pages/DocketBatchPrint"));
const DocketSlip = lazy(() => import("@/pages/DocketSlip"));
const KitchenSlip = lazy(() => import("@/pages/KitchenSlip"));
const TableCheck = lazy(() => import("@/pages/TableCheck"));
const BrowseMenu = lazy(() => import("@/pages/BrowseMenu"));
const ScanQR = lazy(() => import("@/pages/ScanQR"));
const QrPrintSheet = lazy(() => import("@/pages/QrPrintSheet"));
const StaffTraining = lazy(() => import("@/pages/StaffTraining"));
const VettingPortal = lazy(() => import("@/pages/VettingPortal"));
const CorporatePortal = lazy(() => import("@/pages/CorporatePortal"));
const CorpSetPassword = lazy(() => import("@/pages/CorpSetPassword"));
const CorpPay = lazy(() => import("@/pages/CorpPay"));
const SatelliteAccept = lazy(() => import("@/pages/SatelliteAccept"));
const GlSchemaPrint = lazy(() => import("@/pages/GlSchemaPrint"));
const ZReadingPrint = lazy(() => import("@/pages/ZReadingPrint"));
const AliasGate = lazy(() => import("@/pages/AliasGate"));
const GamesArcade = lazy(() => import("@/pages/GamesArcade"));

applyTheme(getStoredTheme());

const RouteFallback = () => (
  <div style={{ minHeight: "100vh", background: "var(--paper, #F8F4EE)" }}>
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "48px 18px" }}>
      <SkelList n={3} lines={2} testId="route-skeleton" />
    </div>
  </div>
);

function App() {
  return (
    <>
      <BrowserRouter>
        <SeoManager />
        <AnalyticsBridge />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<CustomerApp />} />
            <Route path="/menu" element={<BrowseMenu />} />
            <Route path="/games" element={<GamesArcade />} />
            <Route path="/games/:gameId" element={<GamesArcade />} />
            <Route path="/scan" element={<ScanQR />} />
            <Route path="/t/:qrToken" element={<TableCheck />} />
            <Route path="/check/:checkNo" element={<TableCheck />} />
            <Route path="/privacy" element={<PrivacyNotice />} />
            <Route path="/claim" element={<ClaimScreen />} />
            <Route path="/claim/:token" element={<ClaimScreen />} />
            <Route path="/vet/:token" element={<VettingPortal />} />
            <Route path="/corp" element={<CorporatePortal />} />
            <Route path="/corp/set-password/:token" element={<CorpSetPassword />} />
            <Route path="/corp/pay/:token" element={<CorpPay />} />
            <Route path="/satellite/accept/:token" element={<SatelliteAccept />} />
            <Route path="/staff" element={<StaffLogin />} />
            <Route path="/staff/invite/:token" element={<StaffInvite />} />
            <Route path="/staff/set-password/:token" element={<SetPassword />} />
            <Route path="/staff/forgot" element={<ForgotPassword />} />
            <Route path="/staff/portal" element={<StaffPortal />} />
            <Route path="/staff/qr-print" element={<QrPrintSheet />} />
            <Route path="/staff/training" element={<StaffTraining />} />
            <Route path="/invoice/:id" element={<InvoicePrint />} />
            <Route path="/docket-batch/:id" element={<DocketBatchPrint />} />
            <Route path="/docket-slip/:id" element={<DocketSlip />} />
            <Route path="/kitchen-slip/:id" element={<KitchenSlip />} />
            <Route path="/gl-schema" element={<GlSchemaPrint />} />
            <Route path="/z-reading/:date" element={<ZReadingPrint />} />
            {/* catch-all: printed-QR alias resolution (Menu QR), else branded 404 */}
            <Route path="*" element={<AliasGate />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#3A342E",
            color: "#FDFBF8",
            border: "none",
            borderRadius: "14px",
            fontFamily: "Jost, sans-serif",
          },
        }}
      />
    </>
  );
}

export default App;
